import { Button, Input, DatePicker, Flex, Select } from "antd";
import { CalendarFilled, CalendarOutlined, FilterOutlined, FunnelPlotOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { useRef } from "react";
import dayjs from "dayjs";
import styled from "@emotion/styled";
import Utils from "utils";

const FilterWrapper = styled("div")`
	padding: 8px;
	width: 250px;
	display: flex;
	flex-direction: column;
	gap: 8px;
	align-content: center;
	justify-content: center;
`;

const SearchFactory = (column, Icon, filterProps) => {
	const searchText = useRef("");
	let searchInput = null;

	const handleSearch = (selectedKeys, confirm) => {
		confirm();
		searchText.current = selectedKeys[0];
	};

	const handleReset = (clearFilters, confirm) => {
		clearFilters();
		searchText.current = "";
		confirm();
	};

	const filterHelper = (callback) => {
		return (value, record) => {
			let recordValue;
			if (Array.isArray(column.key)) {
				recordValue = column.key.reduce((acc, key) => {
					return acc[key];
				}, record);
			} else {
				recordValue = record[column.key]
			}

			return callback(recordValue, value)
		}
	}

	return {
		align: column.align,
		dataIndex: column.dataIndex,
		title: column.title,
		key: column.key,
		defaultSortOrder: column.defaultSortOrder,
		sorter: column.sorter,
		filterDropdownProps: {
			onOpenChange: (visible) => {
				if (visible) {
					setTimeout(() => searchInput?.select(), 100);
				}
			},
		},
		filterIcon: (filtered) => <Icon style={{ color: filtered ? "#455560" : "#ececec" }} />,
		...filterProps({
			handleSearch,
			handleReset,
			searchInput: (node) => {
				searchInput = node;
			},
			searchText,
			filterHelper,
		}),
		...(column.render ? { render: column.render } : {}),
	};
};

const ColumnSearch = {
	Input: (column) =>
		SearchFactory(column, SearchOutlined, ({handleSearch, handleReset, searchInput, searchText, filterHelper}) => ({
			onFilter: filterHelper((recordValue, value) => recordValue?.toString().toLowerCase().includes(value.toLowerCase())),
			filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
				<FilterWrapper>
					<Input
						ref={(node) => {
							searchInput(node);
						}}
						placeholder={`Buscar ${column.placeholder}`}
						value={selectedKeys[0]}
						onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
						onPressEnter={() => handleSearch(selectedKeys, confirm, setSelectedKeys)}
					/>
					<Flex gap={8}>
						<Button className="flex-1" type="primary" onClick={() => handleSearch(selectedKeys, confirm)} size="small">
							Buscar
						</Button>
						<Button className="flex-1" onClick={() => handleReset(clearFilters, confirm)} size="small">
							Limpar
						</Button>
					</Flex>
				</FilterWrapper>
			),
			render: (text) => (
				<Highlighter
					autoEscape
					highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
					searchWords={[searchText.current]}
					textToHighlight={text ? text.toString() : ""}
				/>
			),
		})),
	DatePicker: (column) =>
		SearchFactory(column, CalendarOutlined, ({handleSearch, filterHelper}) => ({
			onFilter: filterHelper((recordValue, value) => dayjs(recordValue).format("YYYY-MM-DD") === value.format("YYYY-MM-DD")),
			filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
				<FilterWrapper>
					<DatePicker
						format="DD/MM/YYYY"
						onChange={(value) => {
							setSelectedKeys(value ? [value] : []);
							handleSearch(selectedKeys, confirm);
						}}
					/>
				</FilterWrapper>
			),
		})),
	RangePicker: (column) =>
		SearchFactory(column, CalendarFilled, ({handleSearch, filterHelper}) => ({
			onFilter: filterHelper((recordValue, value) => dayjs(recordValue).isAfter(value[0].startOf("day")) && dayjs(recordValue).isBefore(value[1].endOf("day"))),
			filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
				<FilterWrapper>
					<DatePicker.RangePicker
						format="DD/MM/YYYY"
						onChange={(value) => {
							setSelectedKeys(value ? [value] : []);
							handleSearch(selectedKeys, confirm);
						}}
					/>
				</FilterWrapper>
			),
		})),
	Select: (column) =>
		SearchFactory(column, FilterOutlined, ({handleSearch, filterHelper}) => ({
			onFilter: filterHelper((recordValue, value) => recordValue === value),
			filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
				<FilterWrapper>
					<Select
						showSearch
						allowClear
						className="w-100"
						optionFilterProp="children"
						filterOption={(input, option) => Utils.searchString(option.children, input)}
						placeholder={`Selecionar ${column.placeholder}`}
						onChange={(value) => {
							setSelectedKeys(value ? [value] : []);
							handleSearch(selectedKeys, confirm);
						}}
					>
						{column.options.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.label}
							</Select.Option>
						))}
					</Select>
				</FilterWrapper>
			),
		})),
	MultiSelect: (column) =>
		SearchFactory(column, FunnelPlotOutlined, ({handleSearch, filterHelper}) => ({
			onFilter: filterHelper((recordValue, value) => value.includes(recordValue)),
			filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
				<FilterWrapper>
					<Button type="primary" onClick={() => handleSearch(selectedKeys, confirm)} size="small">
						Buscar
					</Button>
					<Select
						showSearch
						allowClear
						mode="multiple"
						optionFilterProp="children"
						filterOption={(input, option) => Utils.searchString(option.children, input)}
						placeholder={`Selecionar ${column.placeholder}`}
						onChange={(value) => {
							setSelectedKeys(value ? [value] : []);
						}}
					>
						{column.options.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.label}
							</Select.Option>
						))}
					</Select>
				</FilterWrapper>
			),
		})),
};

export default ColumnSearch;
